import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import { gridShortDate, isValidDate } from 'utilities';
import { FinancialsDateSelectorProps } from './FinancialsDateSelector.props';

/*
Decouple the date picker's calendar functionality from its default input behavior.
By providing a slot that renders an empty div, we turn it into a headless picker.
We have full authority over the text field's validation, event handling, and
formatting without fighting the date picker's internal masked input logic.
*/
const HiddenField = () => <div />;

const FinancialsDateSelector = ({ onDateChange, currentDate, disabled = false }: FinancialsDateSelectorProps) => {
  const [inputValue, setInputValue] = useState(() => (currentDate ? gridShortDate(currentDate) : ''));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(currentDate ? gridShortDate(currentDate) : '');
    setHasError(false);
  }, [currentDate]);

  const commitDate = useCallback(
    (text: string) => {
      if (!text.trim()) {
        setHasError(false);
        return;
      }
      if (isValidDate(text)) {
        const formatted = gridShortDate(text);
        setInputValue(formatted);
        setHasError(false);
        onDateChange(formatted);
      } else {
        setHasError(true);
      }
    },
    [onDateChange]
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (hasError) {
      setHasError(false);
    }
  };

  const handleBlur = () => commitDate(inputValue);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitDate(inputValue);
    }
  };

  const handlePickerAccept = (date: Date | null) => {
    if (date) {
      const formatted = gridShortDate(date);
      setInputValue(formatted);
      setHasError(false);
      onDateChange(formatted);
    }
    setPickerOpen(false);
  };

  const dateValue = currentDate ? moment(currentDate).toDate() : null;

  return (
    <Box sx={{ ml: 1.5 }} ref={anchorRef}>
      <TextField
        label="Financials Date"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        error={hasError}
        helperText={hasError ? 'Invalid date (MM/DD/YYYY)' : undefined}
        disabled={disabled}
        placeholder="MM/DD/YYYY"
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setPickerOpen(true)}
                  disabled={disabled}
                  size="small"
                  edge="end"
                  aria-label="open date picker"
                >
                  <CalendarIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
      <DatePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        value={dateValue}
        onAccept={handlePickerAccept}
        openTo="day"
        views={['year', 'month', 'day']}
        slots={{
          field: HiddenField,
        }}
        slotProps={{
          popper: {
            anchorEl: anchorRef.current,
          },
        }}
      />
    </Box>
  );
};

export default FinancialsDateSelector;
